import OrderBadge from './parts/OrderBadge'
import OrderMetaInfo from './parts/OrderMetaInfo'
import { ColorEnum } from '@/constants/color'
import State from '@/features/orders/components/OrderListItem/State'
import OrderListItemContext from '@/features/orders/components/OrderListItem/contexts/OrderListItem'
import { Order } from '@/features/orders/types/app'
import { ButtonStateEnum } from '@/features/ui/buttons/constants'
import { useEnabledButtonState } from '@/features/ui/buttons/hooks/useEnabledButtonState'
import { isInteractiveButtonState } from '@/features/ui/buttons/utils'
import { useScreenLayout } from '@/hooks/screen'
import { hexToRgba } from '@/utils/hexToRgba'
import { FC, ReactNode } from 'react'
import { Pressable, StyleProp, View, ViewStyle } from 'react-native'
import { createStyleSheet, useStyles } from 'react-native-unistyles'

interface OrderListItemProps {
    order: Order
    RightEndComponent: ReactNode
    onPress: () => void
    style?: StyleProp<ViewStyle>
}

interface OrderListItemComponent extends FC<OrderListItemProps> {
    State: typeof State
}

const OrderListItem: OrderListItemComponent = ({ order, RightEndComponent, onPress, style }) => {
    const { state, eventHandlers } = useEnabledButtonState()
    const layout = useScreenLayout()

    const { styles } = useStyles(stylesheet, {
        layout,
        state: isInteractiveButtonState(state) ? state : undefined,
    })

    return (
        <OrderListItemContext.Provider value={{ order }}>
            <Pressable {...eventHandlers} onPress={onPress} style={[styles.outerContainer, style]}>
                <View style={styles.innerContainer}>
                    {order.shortCode && (
                        <OrderBadge size="large" textSize="small" style={styles.shortCodeBadge}>
                            {order.shortCode}
                        </OrderBadge>
                    )}
                    <View style={styles.contentContainer}>
                        <OrderMetaInfo
                            deliveryTypeCategory={order.deliveryType.typeCategory}
                            customerName={order.customerName}
                            deliveryAddress={order.deliveryAddress}
                            channel={order.channel}
                            deliveryZone={order.deliveryZone}
                            paymentType={order.paymentType}
                        />
                    </View>
                    {RightEndComponent}
                </View>
            </Pressable>
        </OrderListItemContext.Provider>
    )
}

const stylesheet = createStyleSheet({
    outerContainer: {
        paddingHorizontal: 16,
        variants: {
            state: {
                [ButtonStateEnum.Hovered]: {
                    backgroundColor: hexToRgba(ColorEnum.Jet700, 0.15),
                },
                [ButtonStateEnum.Pressed]: {
                    backgroundColor: hexToRgba(ColorEnum.Jet700, 0.3),
                },
            },
        },
    },
    innerContainer: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        borderBottomWidth: 1,
        borderBottomColor: ColorEnum.GRAY_200,
        paddingVertical: 10,
    },
    contentContainer: {
        flex: 1,
        height: '100%',
        justifyContent: 'space-evenly',
    },
    contentBottomContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
    },
    name: {
        fontSize: 12,
        color: ColorEnum.GRAY_600,
    },
    shortCodeBadge: {
        variants: {
            layout: {
                extended: {
                    marginRight: 12,
                },
                compact: {
                    marginRight: 8,
                },
            },
        },
    },
})

OrderListItem.State = State

export default OrderListItem
